import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { AssistantMessagePayload } from '../../protocol/types';
import { AttachmentChip } from './AttachmentChip';
import { MessageMeta } from './MessageMeta';
import { RenderFrame } from './RenderFrame';

// react-markdown wraps fenced code blocks in <pre><code class="language-X">.
// Intercept <pre> to detect ```render and swap the block for a sandboxed iframe.
const MARKDOWN_COMPONENTS: Components = {
  pre({ children, ...props }) {
    const child = Array.isArray(children) ? children[0] : children;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const codeProps = (child as any)?.props;
    const className: string = codeProps?.className ?? '';
    if (/(?:^|\s)language-render(?:\s|$)/.test(className)) {
      const html = String(codeProps?.children ?? '').replace(/\n$/, '');
      return <RenderFrame html={html} />;
    }
    return <pre {...props}>{children}</pre>;
  },
};

interface Props {
  payload: AssistantMessagePayload;
  timestamp?: number | undefined;
  /** When true the content is an in-progress streaming preview (shows caret). */
  streaming?: boolean;
}

/** An assistant turn — rendered free-standing on the page, no bubble. */
export function AssistantMessage({ payload, timestamp, streaming }: Props) {
  const text = (payload.text ?? '').replace(/\n+$/, '').trimEnd();
  return (
    <div className="animate-fade-in">
      {text || streaming ? (
        <div className={`md text-[15px] leading-relaxed text-text-primary${streaming ? ' streaming' : ''}`}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>{text || ' '}</ReactMarkdown>
        </div>
      ) : null}
      {payload.attachments && payload.attachments.length > 0 ? (
        <div className={`flex flex-wrap gap-1.5 ${text ? 'mt-2' : ''}`}>
          {payload.attachments.map((a) => (
            <AttachmentChip key={a.id} attachment={a} variant="assistant" />
          ))}
        </div>
      ) : null}
      {!streaming ? <MessageMeta timestamp={timestamp} align="left" /> : null}
    </div>
  );
}
